import React, { useState, useEffect, useRef } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { Link, useRouteMatch } from "react-router-dom";
import { apiFetch, formatUiErrors } from "utils";
import { useAuthState, useAuthDispatch } from "context";
import ResyncForm from "./components/ResyncForm";
import ResyncStatus from "./components/ResyncStatus";

const Resync = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const isAdminRoute = useRouteMatch("/admin/shelterluv/resync");
  const jobsPath = isAdminRoute
    ? "/admin/shelterluv/resync-jobs"
    : "/api-management/resync-jobs";

  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [detectOrphans, setDetectOrphans] = useState(false);
  const [steps, setSteps] = useState({
    animalData: "pending",
    eventData: "pending",
    pairing: "pending",
  });

  const animationTimeoutsRef = useRef([]);
  const pollingIntervalRef = useRef(null);
  const stepsRef = useRef(steps);
  const jobStatusRef = useRef(jobStatus);
  const currentJobIdRef = useRef(null);
  const hasShownCompletedToastForJobRef = useRef(new Set());
  const isMountedRef = useRef(true);
  const lastProcessedJobIdRef = useRef(null);
  const completionToastTimeoutRef = useRef(null);

  const initialSteps = (withOrphans) => ({
    animalData: "pending",
    eventData: "pending",
    pairing: "pending",
    ...(withOrphans ? { orphanDetection: "pending" } : {}),
  });

  const { mutate: startResync, isLoading: isStarting } = useMutation(
    (values) =>
      apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/internal/shelterluv/resync",
        body: {
          date_from: values.dateFrom,
          detect_orphans: values.detectOrphans,
          limit_orphans_to_date: values.limitOrphansToDate || false,
          ...(values.organizationId
            ? { organization_id: Number(values.organizationId) }
            : {}),
        },
      }),
    {
      onSuccess: (response, values) => {
        const jobData = response.data;

        if (jobData.jobId) {
          if (completionToastTimeoutRef.current) {
            clearTimeout(completionToastTimeoutRef.current);
            completionToastTimeoutRef.current = null;
          }

          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          animationTimeoutsRef.current.forEach((timeout) =>
            clearTimeout(timeout)
          );
          animationTimeoutsRef.current = [];

          const newJobId = jobData.jobId;
          const withOrphans = values.detectOrphans;

          currentJobIdRef.current = newJobId;
          lastProcessedJobIdRef.current = null;
          setJobId(newJobId);
          setJobStatus("pending");
          setDetectOrphans(withOrphans);
          jobStatusRef.current = "pending";

          const fresh = initialSteps(withOrphans);
          setSteps(fresh);
          stepsRef.current = fresh;

          hasShownCompletedToastForJobRef.current.delete(newJobId);

          toast.success("Resync process started successfully", {
            toastId: `resync-started-${newJobId}`,
          });

          startPolling(newJobId, withOrphans);
        } else {
          toast.error("Error: Could not retrieve Job ID");
        }
      },
      onError: (error) => {
        toast.error(() => formatUiErrors(error));
      },
    }
  );

  const simulateStepProgress = (stepKey, stepStatus) => {
    let delay = 0;

    if (stepStatus === "processing" || stepStatus === "running") {
      if (stepKey === "animalData") delay = 0;
      else if (stepKey === "eventData") delay = 1000;
      else if (stepKey === "pairing") delay = 2000;
      else if (stepKey === "orphanDetection") delay = 3000;
    } else if (stepStatus === "completed") {
      if (stepKey === "animalData") delay = 0;
      else if (stepKey === "eventData") delay = 300;
      else if (stepKey === "pairing") delay = 600;
      else if (stepKey === "orphanDetection") delay = 900;
    }

    return delay;
  };

  const showCompletionToast = (jobId) => {
    if (hasShownCompletedToastForJobRef.current.has(jobId)) {
      return;
    }

    hasShownCompletedToastForJobRef.current.add(jobId);

    if (completionToastTimeoutRef.current) {
      clearTimeout(completionToastTimeoutRef.current);
    }

    completionToastTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && currentJobIdRef.current === jobId) {
        toast.success("Resync completed successfully!", {
          toastId: `resync-completed-${jobId}`,
          autoClose: 5000,
        });
        completionToastTimeoutRef.current = null;
      }
    }, 800);
  };

  const animateSteps = (
    backendSteps,
    targetJobId,
    jobDataStatus,
    withOrphans
  ) => {
    animationTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    animationTimeoutsRef.current = [];

    const stepOrder = [
      { key: "animalData", label: "Animal Data" },
      { key: "eventData", label: "Event Data" },
      { key: "pairing", label: "Pairing" },
      ...(withOrphans
        ? [{ key: "orphanDetection", label: "Orphan Detection" }]
        : []),
    ];

    let maxDelay = 0;

    stepOrder.forEach(({ key }) => {
      const stepStatus = backendSteps[key] || "pending";
      const delay = simulateStepProgress(key, stepStatus);
      maxDelay = Math.max(maxDelay, delay);

      const timeout = setTimeout(() => {
        if (isMountedRef.current && currentJobIdRef.current === targetJobId) {
          setSteps((prev) => ({
            ...prev,
            [key]: stepStatus,
          }));
        }
      }, delay);

      animationTimeoutsRef.current.push(timeout);
    });

    if (jobDataStatus === "completed" && targetJobId) {
      const toastDelay = maxDelay + 300;
      const timeout = setTimeout(() => {
        if (isMountedRef.current && currentJobIdRef.current === targetJobId) {
          showCompletionToast(targetJobId);
        }
      }, toastDelay);

      animationTimeoutsRef.current.push(timeout);
    }
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    animationTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    animationTimeoutsRef.current = [];
  };

  const startPolling = (id, withOrphans) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const fetchJobStatus = async () => {
      if (!isMountedRef.current) return;

      if (currentJobIdRef.current !== id) {
        stopPolling();
        return;
      }

      try {
        const response = await apiFetch({
          token,
          dispatch,
          method: "GET",
          endpoint: `/api/internal/shelterluv/resync/${id}`,
        });

        const jobData = response.data;

        if (jobData.status && jobData.status !== jobStatusRef.current) {
          setJobStatus(jobData.status);
          jobStatusRef.current = jobData.status;

          if (jobData.status === "failed") {
            if (
              currentJobIdRef.current === id &&
              !hasShownCompletedToastForJobRef.current.has(id)
            ) {
              hasShownCompletedToastForJobRef.current.add(id);
              toast.error(
                `Resync failed: ${jobData.error_message || "Unknown error"}`,
                {
                  toastId: `resync-failed-${id}`,
                }
              );
              stopPolling();
            }
          }
        }

        if (jobData.status === "completed") {
          setTimeout(() => {
            if (currentJobIdRef.current === id) {
              stopPolling();
            }
          }, 2000);
        }

        if (jobData.steps) {
          const normalizedSteps = {
            animalData:
              jobData.steps.animalData ||
              jobData.steps.animal_data ||
              "pending",
            eventData:
              jobData.steps.eventData || jobData.steps.event_data || "pending",
            pairing: jobData.steps.pairing || "pending",
            ...(withOrphans
              ? {
                  orphanDetection:
                    jobData.steps.orphanDetection ||
                    jobData.steps.orphan_detection ||
                    "pending",
                }
              : {}),
          };

          const currentStepsStr = JSON.stringify(stepsRef.current);
          const newStepsStr = JSON.stringify(normalizedSteps);

          if (
            lastProcessedJobIdRef.current !== id ||
            currentStepsStr !== newStepsStr ||
            jobData.status === "completed"
          ) {
            lastProcessedJobIdRef.current = id;
            stepsRef.current = normalizedSteps;

            animateSteps(normalizedSteps, id, jobData.status, withOrphans);
          }
        }
      } catch (error) {
        console.error("Error fetching job status:", error);

        if (
          currentJobIdRef.current === id &&
          !hasShownCompletedToastForJobRef.current.has(id)
        ) {
          hasShownCompletedToastForJobRef.current.add(id);
          toast.error("Error fetching job status. Please try again.", {
            toastId: `resync-error-${id}`,
          });
          stopPolling();
        }
      }
    };

    fetchJobStatus();
    pollingIntervalRef.current = setInterval(fetchJobStatus, 2000);
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      stopPolling();
      if (completionToastTimeoutRef.current) {
        clearTimeout(completionToastTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (jobId && !pollingIntervalRef.current) {
      currentJobIdRef.current = jobId;
      startPolling(jobId, detectOrphans);
    }
  }, [jobId]);

  const isProcessing = ["pending", "processing", "running"].includes(jobStatus);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Shelterluv Data Resync</h1>
        <Link to={jobsPath}>
          <button className="text-blue-600 hover:text-blue-800 font-medium">
            View Jobs History →
          </button>
        </Link>
      </div>

      <div className="bg-blue-50 p-4 rounded mb-6 text-sm text-blue-800 border border-blue-100">
        This tool allows you to refresh your Shelterluv data. Select a "Date
        From" below, and we will resync animal data, event data, and update all
        records behind the scenes. Once finished, you can reprocess your monthly
        data.
      </div>

      <div className="bg-white rounded p-6 border shadow-sm max-w-3xl">
        <ResyncForm
          onSubmit={startResync}
          isLoading={isStarting}
          isProcessing={isProcessing}
        />

        {jobId && (
          <ResyncStatus
            steps={steps}
            jobStatus={jobStatus}
            jobId={jobId}
            detectOrphans={detectOrphans}
          />
        )}
      </div>
    </div>
  );
};

export default Resync;
